import { useState } from 'react'
import { useGuardrail, useKillSwitchMutation } from '@/hooks/useGuardrail'
import { cn, formatTime } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Input,
  StatCard,
} from '@/admin-design/_common'

const fmtSigned = (n: number) => {
  if (isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return sign + new Intl.NumberFormat('ko-KR').format(Math.round(n))
}

export default function Guardrail() {
  const q = useGuardrail()
  const mutate = useKillSwitchMutation()

  const [activatePrompt, setActivatePrompt] = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)
  const [reason, setReason] = useState('')

  const data = q.data
  const ks = data?.kill_switch
  const dl = data?.daily_loss

  const orderCount = data?.daily_order_count ?? 0

  // Daily loss progress: realized as a fraction of (limit% × 1_000_000 baseline).
  // We don't have account_equity here; simplify by computing realized vs an
  // implicit "1×limit_pct" budget — operators care about the trend, not exact
  // figures (those live in Telegram alerts).
  const limitPct = dl?.limit_pct ?? -2
  const realized = dl?.realized ?? 0
  const breached = !!dl?.breached
  // Show realized as % of an assumed 10M KRW seed equity if positive; for
  // display only.
  const baseEquity = 10_000_000
  const realizedPct = (realized / baseEquity) * 100
  const lossUsed = Math.min(100, Math.max(0, (realizedPct / limitPct) * 100))

  const onClear = () => {
    mutate.mutate({ active: false })
    setConfirmClear(false)
  }

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Guardrails</h1>
        <p className="text-sm text-muted-foreground">
          Live state of the trading safety nets. Refreshes every 15 seconds.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Daily loss */}
        <Card
          className={cn(
            'space-y-2 px-4 py-3',
            breached && 'border-destructive-soft bg-destructive-soft',
          )}
        >
          <h2 className="text-xs uppercase text-muted-foreground tracking-wide">Daily Loss</h2>
          <p className="text-2xl font-semibold tabular-nums">{fmtSigned(realized)} ₩</p>
          <p className="text-xs text-muted-foreground">
            limit {limitPct}% (≈ {fmtSigned((limitPct / 100) * baseEquity)} ₩)
          </p>
          <div
            className="w-full h-2 bg-muted rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(lossUsed)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              // Daily-loss progress bar ramp — KEEP RAW (data-viz threshold ramp,
              // green/amber/red is intrinsic to the visualization).
              className={cn(
                'h-full transition-all',
                breached ? 'bg-red-600' : lossUsed > 70 ? 'bg-amber-500' : 'bg-green-500',
              )}
              style={{ width: `${lossUsed}%` }}
            />
          </div>
          {breached && (
            <p className="text-xs font-semibold text-destructive">BREACHED — entries blocked</p>
          )}
        </Card>

        {/* Kill switch */}
        <Card
          className={cn(
            'space-y-3 px-4 py-3',
            ks?.active && 'border-destructive-soft bg-destructive-soft',
          )}
        >
          <h2 className="text-xs uppercase text-muted-foreground tracking-wide">Kill Switch</h2>
          <p className="text-2xl font-semibold">{ks?.active ? 'ACTIVE' : 'inactive'}</p>
          {ks?.active && (
            <>
              <p className="text-xs text-foreground">{ks.reason || 'no reason recorded'}</p>
              <p className="text-xs text-muted-foreground">
                tripped {ks.tripped_at ? formatTime(ks.tripped_at) : '—'} · mode {ks.mode || 'manual'}
              </p>
            </>
          )}
          {ks?.active ? (
            <Button variant="primary" className="w-full" onClick={() => setConfirmClear(true)}>
              Resume trading
            </Button>
          ) : (
            <Button variant="destructive" className="w-full" onClick={() => setActivatePrompt(true)}>
              Activate kill switch
            </Button>
          )}
        </Card>

        {/* Order budget */}
        <StatCard
          label="Daily order count"
          value={orderCount}
          hint="Submitted today (KST)"
          tone={orderCount > 100 ? 'warning' : undefined}
        />
      </div>

      {q.error && (
        <Badge variant="destructive" className="rounded">
          Failed to load guardrail snapshot: {(q.error as Error).message}
        </Badge>
      )}

      {mutate.error && (
        <Badge variant="destructive" className="rounded">
          Action failed: {(mutate.error as Error).message}
        </Badge>
      )}

      {/* Activate prompt — ConfirmDialog body slot + confirmDisabled. */}
      <ConfirmDialog
        open={activatePrompt}
        onOpenChange={setActivatePrompt}
        title="Activate kill switch"
        description="Provide a reason for activating the guardrail."
        body={
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason..."
            autoFocus
          />
        }
        confirmDisabled={!reason.trim()}
        confirmLabel="Activate"
        confirmVariant="destructive"
        onConfirm={async () => {
          mutate.mutate({ active: true, reason: reason.trim(), mode: 'manual' })
          setReason('')
          setActivatePrompt(false)
        }}
      />

      {/* Resume confirm — admin-design ConfirmDialog (no input needed). */}
      <ConfirmDialog
        open={confirmClear}
        onOpenChange={(open) => { if (!open) setConfirmClear(false) }}
        title="Resume trading?"
        description="The kill switch will be cleared and entries will be allowed once again."
        confirmLabel="Resume"
        loading={mutate.isPending}
        onConfirm={onClear}
      />
    </div>
  )
}
